/**
 * @fileoverview Composable useModal - API moderna per sistema Modal
 *
 * Composable moderno per la gestione di modal dinamici con Composition API.
 * Fornisce API Promise-based e type-safe mantenendo piena compatibilità.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-25
 */

import { getCurrentInstance } from 'vue';
import type {
	ModalProgrammaticService,
	ModalCallback,
	ModalCallbackData,
	ModalInstance,
} from '@/lib/services/modal/index';

/**
 * Opzioni per l'apertura di un modal
 */
export interface UseModalOptions {
	/** Mostra bottone conferma (default: true) */
	confirmButton?: boolean;
	/** Testo bottone cancellazione (default: 'Annulla') */
	cancelButtonText?: string;
	/** Auto-close modal dopo conferma (default: true) */
	autoClose?: boolean;
}

/**
 * Risultato della conferma modal
 */
export interface ModalConfirmResult<T = any> {
	/** Se l'azione è stata confermata */
	confirmed: boolean;
	/** Dati dal form, disponibili se confirmed = true */
	data?: T;
}

/**
 * Promise-based modal result
 */
export type ModalPromise<T = any> = Promise<ModalConfirmResult<T>>;

/**
 * Composable per gestione modal moderna
 */
export function useModal() {
	// Accesso al servizio modal tramite current instance
	const instance = getCurrentInstance();
	const $modal = (instance?.proxy as any)?.$modal as ModalProgrammaticService;

	if (!$modal) {
		console.warn('⚠️ useModal: $modal service not available');
	}

	/**
	 * Apre un modal con Promise-based API
	 */
	function openModal<T = any>(
		title: string,
		component: any,
		props: Record<string, any> = {},
		options: UseModalOptions = {},
	): ModalPromise<T> & { instance: ModalInstance } {
		const { confirmButton = true, cancelButtonText = 'Annulla', autoClose = true } = options;

		let modalInstance: ModalInstance;

		const promise = new Promise<ModalConfirmResult<T>>((resolve) => {
			const onConfirm: ModalCallback = ({ data }: ModalCallbackData) => {
				if (autoClose && modalInstance) {
					modalInstance.close();
				}
				resolve({
					confirmed: true,
					data: data as T,
				});
			};

			const onCancel: ModalCallback = () => {
				resolve({
					confirmed: false,
				});
			};

			// Crea modal utilizzando il servizio esistente
			modalInstance = $modal.open(
				title,
				component,
				props,
				onConfirm,
				onCancel,
				confirmButton,
				cancelButtonText,
			);
		});

		// Aggiungi riferimento all'istanza per controllo manuale
		(promise as any).instance = modalInstance!;

		return promise as ModalPromise<T> & { instance: ModalInstance };
	}

	/**
	 * Apre un semplice dialog di conferma con messaggio testuale
	 */
	async function confirmDialog(
		title: string,
		message: string,
		options: UseModalOptions = {},
	): Promise<boolean> {
		// Componente semplice per mostrare solo il messaggio
		const MessageComponent = {
			template: `<div class="text-gray-700">${message}</div>`,
		};

		const result = await openModal(title, MessageComponent, {}, options);
		return result.confirmed;
	}

	return {
		/** API moderna Promise-based */
		openModal,
		confirmDialog,

		/** Accesso diretto al servizio per retrocompatibilità */
		modalService: $modal,

		/** Alias per compatibilità */
		$modal,
	};
}

/**
 * Type helper per inferire il tipo di data da un componente form
 */
export type InferModalData<T> = T extends { $emit: (event: 'change', data: infer D) => void }
	? D
	: any;

/**
 * Utility type per definire props di un modal form component
 */
export interface ModalFormComponent<TData = any, TProps = any> {
	/** Props del componente */
	props?: TProps;
	/** Data emessa dal form */
	emitChange: (data: TData) => void;
	/** Validation state */
	emitValidate: (isValid: boolean) => void;
}

/**
 * Re-export dei tipi per convenienza
 */
export type { ModalProgrammaticService, ModalCallback, ModalCallbackData, ModalInstance };
